﻿import { htmlEncode, tryGetText } from "@serenity-is/corelib/q";
import { RemoteView } from "@serenity-is/corelib/slick";
import { Column, EventEmitter, EventSubscriber, Grid } from "@serenity-is/sleekgrid";

export enum HeaderFilterType {
    disabled = 0,
    value = 1,
    text = 2
}

declare module "@serenity-is/sleekgrid" {
    interface Column<TItem = any> {
        headerFilterType?: HeaderFilterType;
        headerFilterValues?: object[];
    }
}

/*
    Based on SlickGrid Header Menu Plugin (https://github.com/mleibman/SlickGrid/blob/master/plugins/slick.headermenu.js)
    Based on SlickGrid Spreadsheet Plugins (https://github.com/danny-sg/slickgrid-spreadsheet-plugins/blob/master/ext.headerfilter.js)
    (Can't be used at the same time as the header menu plugin as it implements the dropdown in the same way)
*/

export interface HeaderFiltersOptions {
    buttonImage?: string;
    buttonClass?: string;
    useColumnFormatter?: boolean;
    getFilterValues?: (column: Column, callback: (values: object[], texts?: string[]) => void) => void;
    getFilterType?: (column: Column) => HeaderFilterType;
}

export class HeaderFiltersPlugin {

    private grid: Grid;
    private menu: JQuery;
    private searchInput: JQuery;
    private handler: EventSubscriber;
    private options: HeaderFiltersOptions;
    private checkedValues : any = new window['Set']();
    private filterValues: object[];
    private filterTexts: string[];
    public onFilterApplied = new EventEmitter();
    public onCommand = new EventEmitter();

    constructor(options: HeaderFiltersOptions) {
        this.handler = new EventSubscriber();

        var defaults: HeaderFiltersOptions = {
            buttonClass: "headerfilterbutton",
            useColumnFormatter: true,
            getFilterValues: null
        };

        this.options = $.extend(true, {}, defaults, options);
        this.handleMenuItemClick = this.handleMenuItemClick.bind(this);
    }
    
    init(g: Grid) {
        
        this.grid = g;
        this.handler.subscribe(g.onHeaderCellRendered, this.handleHeaderCellRendered.bind(this))
            .subscribe(g.onBeforeHeaderCellDestroy, this.handleBeforeHeaderCellDestroy.bind(this))
            .subscribe(g.onClick, this.handleBodyMouseDown.bind(this))
            .subscribe(g.onColumnsResized, this.columnsResized.bind(this));

        g.setColumns(g.getColumns());

        $(document.body).bind("mousedown", this.handleBodyMouseDown = this.handleBodyMouseDown.bind(this));
    }

    destroy() {
        this.handler.unsubscribeAll();
        $(document.body).unbind("mousedown", this.handleBodyMouseDown);
    }

    getFilterType(column: Column) {
        if (column.headerFilterType != null)
            return column.headerFilterType;

        if (this.options.getFilterType != null) {
            column.headerFilterType = this.options.getFilterType(column);
            if (column.headerFilterType != null)
                return column.headerFilterType;
        }

        if (column.headerFilterType == null)
            column.headerFilterType = HeaderFilterType.value;

        return column.headerFilterType;
    }

    private handleBodyMouseDown(e: any) {
        if (this.menu && this.menu[0] != e.target && !$.contains(this.menu[0], e.target)) {
            this.hideMenu();
        }
    }

    private hideMenu() {
        if (this.menu) {
            this.menu.remove();
            this.menu = null;
        }
    }

    private handleHeaderCellRendered(e: any, args: any) {
        var column = args.column as Column;

        var filterType = this.getFilterType(column);
        if (filterType === HeaderFilterType.disabled)
            return false;

        var $el = $("<span></span>")
            .addClass("slick-header-menubutton s-header-filter-button")
            .data("column", column);

        if (this.options.buttonImage) {
            $el.css("background-image", "url(" + this.options.buttonImage + ")");
        }

        this.setButtonIsFiltered($el, column.headerFilterValues != null && column.headerFilterValues.length > 0);

        $(args.node).addClass('has-header-menubutton');
        $el.on("click", e => {
            e.stopPropagation();
            this.showFilter(e);
        }).appendTo(args.node);
    }

    private handleBeforeHeaderCellDestroy(e: any, args: any) {
        $(args.node)
            .find(".s-header-filter-button")
            .remove();
    }

    private addMenuItem(menu: JQuery, columnDef: Column, title: string, command: string, image: string) {
        var $item = $("<div class='slick-header-menuitem s-header-filter-item'>")
            .data("command", command)
            .data("column", columnDef)
            .bind("click", this.handleMenuItemClick)
            .appendTo(menu);

        var $icon = $("<div class='slick-header-menuicon s-header-filter-icon'>")
            .appendTo($item);

        if (image) {
            $icon.css("background-image", "url(" + image + ")");
        }

        $("<span class='slick-header-menucontent s-header-filter-content'>")
                .text(title)
                .appendTo($item);
    }

    private addSearchInput(menu: JQuery, columnDef: Column) {
        return $('<input class="input" placeholder="'
            + (htmlEncode(tryGetText("Site.HeaderFiltersMixin.Search") || "Search"))
            + '">')
            .data("column", columnDef)
            .bind("keyup", (e) => {
                this.updateFilterValues(columnDef, () => {});
            })
            .appendTo(menu);
    }

    private updateFilterItems(columnDef: Column) {
        var filterOptions = "<label><input type='checkbox' value='-1' />" +  
            (htmlEncode(tryGetText("Site.HeaderFiltersMixin.SelectAll") || "(Select All)")) + "</label>";

        this.checkedValues = new window['Set'](columnDef.headerFilterValues || []);

        for (var i = 0; i < this.filterValues.length; i++) {
            var filtered = this.checkedValues.has(this.filterValues[i]);
            filterOptions += "<label><input type='checkbox' value='" + i + "'"
                + (filtered ? " checked='checked'" : "")
                + "/>" + this.filterTexts[i] + "</label>";
        }

        var $filter = this.menu.find('.filter');
        $filter.empty().append($(filterOptions));

        $(':checkbox', $filter).bind('click', e => {
            this.onCheckboxClick($(e.target));
        });
    }

    private getFilterText(item: any, column: Column): string {
        var value = item[column.field];

        if (this.options.useColumnFormatter) {
            try {
                var format = this.grid.getFormatter(-1, column);
                if (format) {
                    var ctx = this.grid.getFormatterContext(-1, -1);
                    ctx.item = item;
                    ctx.value = value;
                    var html = format(ctx);

                    if (value == null && html === '') {
                        return (htmlEncode(tryGetText("Site.HeaderFiltersMixin.Null") || "(null)"));
                    }

                    return html;
                }
            }
            catch (ex) {
            }
        }

        if (value == null)
            return (htmlEncode(tryGetText("Site.HeaderFiltersMixin.Null") || "(null)"));

        return htmlEncode(value);
    }

    private showFilter(e: any) {
        var $menuButton = $(e.target);
        var columnDef = $menuButton.data("column") as Column;

        this.checkedValues = new window['Set'](columnDef.headerFilterValues || []);

        if (!this.menu)
            this.menu = $("<div class='slick-header-menu s-header-filter-menu cke_dialog'>").appendTo(document.body).hide();
        else
            this.menu.empty();

        this.searchInput = this.addSearchInput(this.menu, columnDef);

        var filterOptions = "<label><input type='checkbox' value='-1' />" + 
            (htmlEncode(tryGetText("Site.HeaderFiltersMixin.SelectAll") || "(Select All)")) + "</label>";

        var $filter = $("<div class='filter'>")
            .appendTo(this.menu);

        var buttons = $('<div class="buttons"></buttons>').appendTo(this.menu);

        $('<button class="btn btn-success">' + (htmlEncode(tryGetText("Site.HeaderFiltersMixin.OkButton") || "OK")) + '</button>')
            .appendTo(buttons)
            .bind('click', ev => {
                if (Array['from']) {
                    columnDef.headerFilterValues = Array['from'](this.checkedValues);
                }
                else {
                    columnDef.headerFilterValues = [];
                    this.checkedValues && this.checkedValues.forEach((x: any) => columnDef.headerFilterValues.push(x));
                }
                this.setButtonIsFiltered($menuButton, columnDef.headerFilterValues.length > 0);
                this.handleApply(ev, columnDef);
            });

        $('<button class="btn btn-danger">' + (htmlEncode(tryGetText("Site.HeaderFiltersMixin.ClearButton") || "Clear")) + '</button>')
            .appendTo(buttons)
            .bind('click', ev => {
                if (columnDef.headerFilterValues)
                    columnDef.headerFilterValues.length = 0;
                this.setButtonIsFiltered($menuButton, false);
                this.handleApply(ev, columnDef);
            });

        $('<button class="btn btn-secondary">' + (htmlEncode(tryGetText("Site.HeaderFiltersMixin.CancelButton") || "Cancel")) + '</button>')
            .appendTo(buttons)
            .bind('click', e => this.hideMenu());

        $(':checkbox', $filter).bind('click', (e) => {
            this.onCheckboxClick($(e.target));
        });

        this.updateFilterValues(columnDef, () => {
            this.menu.show();
            $filter.css('height', null);
            var menuHeight = this.menu.height();
            var buttonHeight = $(e.target).height();

            var offset = $(e.target).offset();
            var left = offset.left - this.menu.width() + $(e.target).width() - 8;

            var menuTop = offset.top + buttonHeight;

            var windowScrollTop = $(window).scrollTop();
            var bottomBoundary = $(window).height() + windowScrollTop;
            var contentWrapperTop = (parseInt($('div.content-wrapper').css('padding-top'), 10) || 0);

            if (menuTop + menuHeight > bottomBoundary &&
                bottomBoundary - menuTop + contentWrapperTop < menuTop - windowScrollTop) {
                menuTop -= (menuHeight + buttonHeight + 8);

                if (menuTop < windowScrollTop)
                    menuTop = windowScrollTop;
            }

            var extraHeight = menuTop + menuHeight - bottomBoundary;

            if (extraHeight > 0) {
                $filter.css('height', ($filter.height() - extraHeight));
            }

            this.menu.css("top", menuTop).css("left", (left > 0 ? left : 0));
        });
    }

    private columnsResized() {
        this.hideMenu();
    }

    private onCheckboxClick($checkbox: JQuery) {
        var index = $checkbox.val();
        var $filter = $checkbox.parent().parent();

        if ($checkbox.val() < 0) {
            if ($checkbox.prop('checked')) {
                $(':checkbox', $filter).prop('checked', true);
                this.checkedValues = new window['Set'](this.filterValues || []);
            } else {
                $(':checkbox', $filter).prop('checked', false);
                this.checkedValues.clear();
            }
        } else {
            var value = this.filterValues[index];
            var has = this.checkedValues.has(value);

            if ($checkbox.prop('checked') && !has) {
                this.checkedValues.add(value);
            }
            else if (has) {
                this.checkedValues.delete(value);
            }
        }
    }

    private setButtonIsFiltered($el: JQuery, filtered: boolean) {
        $el.toggleClass('is-filtered', filtered);
    }

    private handleApply(e: any, columnDef: Column) {
        this.hideMenu();

        this.onFilterApplied.notify({ 
            "grid": this.grid, 
            "column": columnDef 
        }, e, this);

        e.preventDefault();
        e.stopPropagation();
    }

    private containsFilter(filter: string): ((text: string) => boolean) {
        if (filter == null && filter.length == 0)
            return v => true;

        var lowercaseFilter = htmlEncode(filter).toLowerCase();

        return (text: string) => {
            text = (text ?? '').toString().replace(/<[^>]+>/g, '').toLowerCase();
            return text.indexOf(lowercaseFilter) >= 0;
        };
    }

    public getFilterValue(item: any, column: Column) {
        var value = item[column.field];
        if (this.options.useColumnFormatter &&
            column.formatter != null &&
            column.headerFilterType == HeaderFilterType.text) {
            return (this.getFilterText(item, column)
                .replace(/<[^>]+>/g, ''));
        }

        return value;
    }

    private sortFilterValues() {
        var idx: number[] = [];
        for (var i = 0; i < this.filterValues.length; i++) {
            idx[i] = i;
        }
        idx.sort((x, y) => {
            var A = this.filterValues[x];
            var B = this.filterValues[y];
            return (A < B ? -1 : (A > B ? 1 : 0));
        });
        var newFilterValues = [];
        var newFilterTexts = [];
        for (var i = 0; i < idx.length; i++) {
            newFilterValues.push(this.filterValues[i]);
            newFilterTexts.push(this.filterTexts[i]);
        }
        this.filterValues = newFilterValues;
        this.filterTexts = newFilterTexts;
    }

    private updateFilterValues(column: Column, done: () => void) {
        if (this.options.getFilterValues != null) {
            this.options.getFilterValues(column, (values: object[], texts: string[]) => {
                if (values == null) {
                    this.updateFilterValuesFromData(column);
                    this.updateFilterItems(column);
                    done();
                }
                else {
                    this.filterValues = [];
                    this.filterTexts = [];

                    texts = texts || values.map(x => {
                        var item: any = {};
                        item[column.field] = x;
                        return this.getFilterText(item, column);
                    });

                    var filter = this.containsFilter(this.searchInput.val());
                    this.filterValues = values.filter((x, i) => {
                        if (filter(texts[i])) {
                            this.filterTexts.push(texts[i]);
                            return true;
                        }

                        return false;
                    });

                    this.sortFilterValues();
                    this.updateFilterItems(column);
                    done();
                }
            });
        }
        else {
            this.updateFilterValuesFromData(column);
            this.updateFilterItems(column);
            done();
        }
    }

    private updateFilterValuesFromData(column: Column) {

        this.filterValues = [];
        this.filterTexts = [];
        var dataView = ((this.grid.getData() as any) as RemoteView<any>);
        
        var items: any[];
        if (this.checkedValues && this.checkedValues.size) {
            items = dataView.getItems();
        }
        else {
            items = [];
            for (var i = 0; i < dataView.getLength(); i++) {
                items.push(dataView.getItem(i));
            }
        }

        var seen = new window['Set']();
        var searchText = this.searchInput.val();
        var searchFilter = this.containsFilter(searchText);
        for (var item of items) {
            var value = this.getFilterValue(item, column);
            if (!seen.has(value)) {
                var text = this.getFilterText(item, column);
                if (searchFilter(text)) {
                    seen.add(value);
                    this.filterValues.push(value);
                    this.filterTexts.push(text);
                }
            }
        }
            
        this.sortFilterValues();
    }

    private handleMenuItemClick(e: any) {
        var command = $(this).data("command");
        var columnDef = $(this).data("column");

        this.hideMenu();

        this.onCommand.notify({
            "grid": this,
            "column": columnDef,
            "command": command
        }, e, this);

        e.preventDefault();
        e.stopPropagation();
    }
}

typeof globalThis != "undefined" && globalThis["Slick"] && globalThis["Slick"]["Plugins"] && (globalThis.Slick["Plugins"]["HeaderFilters"] = HeaderFiltersPlugin);
